import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, trackLaunch } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

// Hosts an AI-generated app in a sandboxed iframe (opaque origin: no cookies, no direct API access).
// The app talks to Jixxeo only through this postMessage bridge, using the member's existing Jixxeo session.
export function useJixxeoBridge(frameRef, slug, onLog) {
  const { user, loading } = useAuth();
  const authRef = useRef({ user, loading });
  authRef.current = { user, loading };
  useEffect(() => {
    const handler = async (e) => {
      if (!frameRef.current || e.source !== frameRef.current.contentWindow) return;
      const d = e.data || {};
      if (d.__jxlog) { onLog?.(d); return; }
      if (!d.__jx) return;
      const reply = (result, error) => e.source.postMessage({ __jx: 1, id: d.id, result, error }, "*");
      try {
        if (d.method === "ping") return reply(true);
        if (d.method === "getUser") {
          for (let i = 0; i < 50 && authRef.current.loading; i++) await new Promise((r) => setTimeout(r, 100));
          const u = authRef.current.user;
          return reply(u ? { id: u.id, name: u.name || u.email.split("@")[0], avatar_color: u.avatar_color } : null);
        }
        if (d.method === "signIn") { window.location.href = `/login?next=${encodeURIComponent(window.location.pathname)}`; return reply(true); }
        if (d.method.startsWith("db.")) {
          const r = await api.post(`/gen/${slug}/data`, { method: d.method, args: d.args });
          return reply(r.data.result);
        }
        reply(null, "Unknown method");
      } catch (err) { reply(null, err?.response?.data?.detail || "Request failed"); }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [frameRef, slug, onLog]);
}

export default function GeneratedAppHost() {
  const { slug } = useParams();
  const ref = useRef(null);
  const [meta, setMeta] = useState(null);
  const [err, setErr] = useState("");
  useJixxeoBridge(ref, slug);
  useEffect(() => {
    api.get(`/gen/${slug}/meta`).then((r) => { setMeta(r.data); trackLaunch(slug); }).catch(() => setErr("App not found"));
  }, [slug]);
  if (err || (meta && !meta.live)) return <div className="min-h-screen flex items-center justify-center text-white/60" data-testid="gen-host-missing">This app isn't available. <Link to="/browse" className="text-[#00F0FF] ml-2">Browse apps</Link></div>;
  if (!meta) return <div className="min-h-screen bg-black" />;
  return (
    <div className="fixed inset-0 bg-black flex flex-col" data-testid="gen-host">
      {window.self === window.top && <div className="h-9 shrink-0 flex items-center gap-3 px-3 bg-black border-b border-white/10 text-xs">
        <Link to={`/app/${slug}`} className="mono uppercase tracking-widest text-white/50 hover:text-white" data-testid="gen-host-back">← JixxeoApps</Link>
        <span className="font-bold text-white/80 truncate">{meta.name}</span>
      </div>}
      <iframe ref={ref} title={meta.name} src={`${process.env.REACT_APP_BACKEND_URL}/api/gen/${slug}/app.html`}
        sandbox="allow-scripts allow-forms allow-popups allow-modals allow-pointer-lock" allow="autoplay; fullscreen; clipboard-write"
        className="flex-1 w-full border-0 bg-white" data-testid="gen-host-frame" />
    </div>
  );
}
